/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './styles.module.scss';
import { Actionable, Text, View } from '@az/starc-ui';
import { useMemo, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useLabels } from '@/hooks/useLabels';
import React from 'react';

type ContentProps = {
  text: string;
  detailsModal?: boolean | undefined;
  truncate?: boolean;
  selectedKeyword?: string;
};

const labelMap = {
  contentAnnounced: 'label_ada_contentAnnounced',
  seeMore: 'label_reviews_seeMore',
  seeLess: 'label_reviews_seeLess',
};

export const Content = ({ text, detailsModal, truncate = true, selectedKeyword }: ContentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const labels = useLabels(labelMap);

  const { isLongText } = useMemo(() => {
    if (!truncate) {
      return { isLongText: false };
    }
    const maxLength = isMobile ? 415 : 680;
    return { isLongText: text.length > maxLength };
  }, [text, truncate, isMobile]);

  const moreOrLessTextLabel = isOpen ? labels.seeLess : labels.seeMore;

  const showTextWithKeyword = (keyword: string) => {
    const splitText = text.toUpperCase().split(keyword.toUpperCase());
    let currentPosition = 0;

    return splitText.map((substring, index) => {
      if (substring === '' && index === splitText.length - 1) {
        return;
      } else if (substring === '' && index === 0) {
        const textElement = (
          <span key={index} className={styles.keyword}>
            {text.slice(currentPosition, currentPosition + keyword.length)}
          </span>
        );
        currentPosition += keyword.length;
        return textElement;
      } else if (index !== splitText.length - 1) {
        const textElement = (
          <React.Fragment key={index}>
            {text.slice(currentPosition, currentPosition + substring.length)}
            <span className={styles.keyword}>
              {text.slice(
                currentPosition + substring.length,
                currentPosition + substring.length + keyword.length,
              )}
            </span>
          </React.Fragment>
        );
        currentPosition += substring.length + keyword.length;
        return textElement;
      } else {
        return <React.Fragment key={index}>{text.slice(currentPosition)}</React.Fragment>;
      }
    });
  };

  if (!detailsModal) {
    return (
      <View className={styles.textContainer}>
        <Text as="span" variant="main-body" className={isLongText && !isOpen && styles.longText}>
          {selectedKeyword ? showTextWithKeyword(selectedKeyword) : text}
        </Text>
        {isLongText && (
          <Actionable
            attributes={{
              'aria-disabled': true,
              'aria-label': `${labels.contentAnnounced} (${moreOrLessTextLabel})`,
            }}
            className={styles.seeLessButton}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <Text variant="inline-link" decoration="underline">
              {moreOrLessTextLabel}
            </Text>
          </Actionable>
        )}
      </View>
    );
  }
  return (
    <View className={styles.modalTextContainer}>
      <Text as="p" variant="main-body">
        {text}
      </Text>
    </View>
  );
};
